import React, { useContext } from "react";

import { DispatchContext } from "../../../context/domain/planning/DispatchContext";
import { WeeklyContext } from "../../../context/global/WeeklyContext";
import { usePlanningContext } from "../../../context/domain/planning/PlanningContext";

import { Spinner } from "../../../components/ui";
import { SwitchVertical } from "../../../components/icons/svg/outline";
import DispatchFiltersPanel from "./DispatchFiltersPanel";
import DispatchSwitcher from "./DispatchSwitcher";

import { formatDateRange } from "../../../utils/datetime";

/**
 * Renders the top bar of the daily dispatch feature. It displays the date range,
 * the Dispatch filters and update-sort icon buttons, a loading spinner, and the
 * dispatch switcher.
 *
 * @component
 * @example
 * return (
 *   <TopBar />
 * )
 *
 * @returns {JSX.Element} The rendered TopBar component.
 */
const TopBar = () => {
  const { dateRange, reloadJobs } = useContext(WeeklyContext);
  const { isReadOnly, isLoading } = useContext(DispatchContext);
  const { planningLastUpdated } = usePlanningContext();

  const dateRangeString = formatDateRange(...dateRange);

  // Both side groups are flex-1 (equal width) so the last-updated text in the
  // middle stays centered on the full screen width.
  return (
    <div className="flex flex-row bg-gray-200 items-center w-full border-b-2 border-gray-300 py-1">
      <div className="flex flex-1 flex-row items-center">
        <div className="font-bold text-md ml-2 mr-4 items-center justify-center">
          <p>{dateRangeString}</p>
        </div>
      </div>
      <div className="shrink-0 text-xs truncate m-0 ml-[2%] p-0">
        {planningLastUpdated}
      </div>
      <div className="mr-4 flex flex-1 items-center justify-end">
        <div className="min-w-[20px]">
          {isLoading && (
            <Spinner
              variant="oval"
              size="h-5 w-5"
              color="#4fa94d"
              secondaryColor="#4fa94d"
              strokeWidth={4}
              strokeWidthSecondary={4}
            />
          )}
        </div>
        <div className="mx-4 flex items-right gap-2">
          <DispatchFiltersPanel />
          {!isReadOnly && (
            <button
              type="button"
              aria-label="Update sort"
              title="Update sort"
              className="inline-flex h-6 items-center rounded-md border border-gray-300 bg-white px-2 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-900 focus:ring-offset-1"
              onClick={reloadJobs}
            >
              <SwitchVertical className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
        <div>
          <DispatchSwitcher />
        </div>
      </div>
    </div>
  );
};

export default TopBar;
